import React, { useState } from 'react';
import styled from '@emotion/styled';
import { useInventoryApi } from '~Components/InventoryUniverse';
import { gql, useQuery } from '@apollo/client';
import { useI18n, useSelector } from '~Hooks';
import Modal from './Modal';
import Button from '~Components/Button';
import Override from './Override';
import MoveStock from './MoveStock';
import RegionalInventory from './RegionalInventory';
import { GetVenues, GetVenuesVariables } from './__queries__';
import RegisterWaste from './RegisterWaste';

const Wrapper = styled.div`
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 9999;
  display: flex;
  flex-flow: row wrap;
  justify-content: center;
  align-items: center;
`;

const Backdrop = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.58);
`;

const ModalWrapper = styled.div`
  z-index: 1;
  position: relative;
  text-align: center;
`;

const InfoWrapper = styled.div`
  padding: 0 48px;
`;

const Text = styled.p`
  font-size: 20px;
  letter-spacing: 0.21px;
  line-height: 20px;
  margin: 0 0 4px;
  color: #41474f;
`;

const Quantity = styled.div`
  font-size: 32px;
  color: #41474f;
  letter-spacing: 0.36px;
`;

const Description = styled.div`
  font-size: 16px;
  color: #41474f;
  letter-spacing: 0.36px;
  margin-bottom: 16px;
`;

const ButtonsWrapper = styled.div`
  padding: 36px 48px 24px;
  margin: auto 0;

  div {
    margin-bottom: 16px;
  }
`;

const StartModalWrapper = styled.div`
  display: flex;
`;

const DescriptionModalWrapper = styled.div`
  max-height: 90vh;
  margin-left: 16px;
  overflow: hidden;
`;

const ArticleImage = styled.img`
  width: 300px;
  border-radius: 8px;
  margin-bottom: 16px;
`;

const VENUES_QUERY = gql`
  query GetVenues($companyId: ID!) {
    venues: allVenues(filter: { deleted: false, active: true, company: { id: $companyId } }) {
      id
      name
    }
  }
`;

interface Props {
  onCancel: () => void;
  id: string;
  venueId: string;
  defaultValue: number;
  isOutOfStock: boolean;
  title: string;
  description?: string;
  imageUrl?: string;
}

export type Screens = 'start' | 'move' | 'override' | 'inventory' | 'waste';

const Details = ({
  onCancel,
  id,
  defaultValue,
  isOutOfStock,
  venueId,
  title,
  description,
  imageUrl,
}: Props) => {
  const { i18n } = useI18n();
  const inventory = useInventoryApi(venueId);
  const companyId = useSelector((state) => state.company);
  const [currentScreen, setCurrentScreen] = useState<Screens>('start');
  const [closing, setClosing] = useState<boolean>(false);

  const { data } = useQuery<GetVenues, GetVenuesVariables>(VENUES_QUERY, {
    variables: {
      companyId,
    },
  });

  function handleCancel() {
    if (closing) {
      return;
    }
    setClosing(true);
    setTimeout(onCancel, 200);
  }

  function handleSetOutOfStock() {
    inventory.setOutOfStock(id, true);
    handleCancel();
  }

  function handleRemoveOutOfStock() {
    inventory.setOutOfStock(id, false);
  }

  function goBack() {
    setCurrentScreen('start');
  }

  function clearInventory() {
    inventory.clearInventory(id);
    handleCancel();
  }

  const venues = data?.venues ?? [];
  const hasMultipleVenues = venues.length > 1;

  return (
    <Wrapper>
      <Backdrop onTouchStart={handleCancel} />
      <ModalWrapper>
        {currentScreen === 'start' && (
          <StartModalWrapper>
            <Modal title={title} small={!hasMultipleVenues} height={defaultValue ? 601 : 536}>
              <InfoWrapper>
                <Text>{i18n('Inventory.Balance')}</Text>
                <Quantity>{isOutOfStock ? i18n('Inventory.OutOfStock') : defaultValue}</Quantity>
              </InfoWrapper>
              <ButtonsWrapper>
                <Button
                  text={i18n('Waste.Register')}
                  disabled={isOutOfStock}
                  handleClick={() => setCurrentScreen('waste')}
                />
                {!isOutOfStock && (
                  <Button
                    text={i18n('Inventory.Override')}
                    handleClick={() => setCurrentScreen('override')}
                  />
                )}
                {isOutOfStock ? (
                  <Button
                    text={i18n('Inventory.RemoveOutOfStock')}
                    handleClick={handleRemoveOutOfStock}
                  />
                ) : (
                  <Button
                    text={i18n('Inventory.SetOutOfStock')}
                    handleClick={handleSetOutOfStock}
                  />
                )}
                {!isOutOfStock && (
                  <Button text={i18n('Inventory.Clear')} handleClick={clearInventory} />
                )}
                {hasMultipleVenues && (
                  <>
                    <Button
                      text={i18n('Inventory.SeeRegional')}
                      handleClick={() => setCurrentScreen('inventory')}
                    />
                    <Button
                      text={i18n('Inventory.MakeTransfer')}
                      handleClick={() => setCurrentScreen('move')}
                      disabled={isOutOfStock || defaultValue == 0}
                    />
                  </>
                )}
              </ButtonsWrapper>
            </Modal>
            {description && (
              <DescriptionModalWrapper>
                {imageUrl && <ArticleImage src={`${imageUrl}/300x300`} />}
                <Modal
                  title={i18n('Inventory.Description')}
                  // if we don't have an image, allow for a larger description-modal.
                  height={imageUrl ? 320 : 601}
                  maxHeight={imageUrl ? 320 : 601}
                  maxWidth="300px">
                  <Description>{i18n(description)}</Description>
                </Modal>
              </DescriptionModalWrapper>
            )}
          </StartModalWrapper>
        )}
        {currentScreen === 'override' && (
          <Modal title={i18n('Inventory.Override')}>
            <Override
              venueId={venueId}
              itemId={id}
              defaultValue={defaultValue}
              isOutOfStock={isOutOfStock}
              goBack={goBack}
              handleClose={handleCancel}
            />
          </Modal>
        )}
        {currentScreen === 'inventory' && (
          <Modal title={i18n('Inventory.Regional')}>
            <RegionalInventory itemId={id} goBack={goBack} />
          </Modal>
        )}
        {currentScreen === 'move' && (
          <Modal title={i18n('Inventory.MakeTransfer')}>
            <MoveStock
              venueId={venueId}
              itemId={id}
              itemName={title}
              itemQty={defaultValue}
              goBack={goBack}
              handleClose={handleCancel}
            />
          </Modal>
        )}
        {currentScreen === 'waste' && (
          <Modal title={i18n('Waste.Register')}>
            <RegisterWaste itemId={id} goBack={goBack} handleCancel={handleCancel} />
          </Modal>
        )}
      </ModalWrapper>
    </Wrapper>
  );
};

export default Details;
